/**
 * Client testimonials carousel — Figma 1:382.
 * Desktop: three-card stage with a simple horizontal slide on prev/next.
 * Mobile: single card with prev/next arrow buttons.
 */
(function () {
  const PIN_ICON =
    '<svg class="mct__pin" viewBox="0 0 11 14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5.5 0C3.015 0 1 2.015 1 4.5c0 3.375 4.5 9.5 4.5 9.5s4.5-6.125 4.5-9.5C10 2.015 7.985 0 5.5 0Zm0 6.125a1.625 1.625 0 1 1 0-3.25 1.625 1.625 0 0 1 0 3.25Z"/></svg>';

  const TRANSITION_MS = 520;
  const MOBILE_MQ = '(max-width: 749px)';

  const namespace = (window.MatchaClientTestimonials = window.MatchaClientTestimonials || {});
  namespace.controllers = namespace.controllers || new WeakMap();

  function init(root) {
    if (root.dataset.mctReady === 'true') return;
    root.dataset.mctReady = 'true';

    namespace.controllers.get(root)?.abort();
    const controller = new AbortController();
    namespace.controllers.set(root, controller);
    const { signal } = controller;

    const token = root.dataset.apiToken?.trim();
    const shopDomain = root.dataset.shopDomain?.trim();
    const sort = root.dataset.sort || 'highest_rating';
    const stage = root.querySelector('[data-mct-stage]');
    const statusEl = root.querySelector('[data-mct-status]');
    const mobileViewport = root.querySelector('[data-mct-mobile-viewport]');
    const mobilePrev = root.querySelector('[data-mct-mobile-prev]');
    const mobileNext = root.querySelector('[data-mct-mobile-next]');
    const desktopPrev = root.querySelector('[data-mct-desktop-prev]');
    const desktopNext = root.querySelector('[data-mct-desktop-next]');
    const mq = window.matchMedia(MOBILE_MQ);

    if (!stage) return;

    const slots = {
      left: stage.querySelector('[data-slot="left"]'),
      center: stage.querySelector('[data-slot="center"]'),
      right: stage.querySelector('[data-slot="right"]'),
      incoming: stage.querySelector('[data-slot="incoming"]'),
    };

    const fallbacks = parseFallbacks(root);
    let items = [];
    let index = 0;
    let isAnimating = false;
    let controlsBound = false;
    let hydrated = false;
    let hasUserNavigated = false;
    let pendingRefresh = false;
    let loadId = 0;

    const clampIndex = () => {
      if (!items.length) {
        index = 0;
        return;
      }
      if (index >= items.length) index = items.length - 1;
      if (index < 0) index = 0;
    };

    const markNavigated = () => {
      hasUserNavigated = true;
    };

    const refreshView = () => {
      if (!items.length) return;

      if (mq.matches) {
        renderMobile();
        return;
      }

      stage.removeAttribute('data-slide');
      render();
    };

    const renderMobile = () => {
      if (!mq.matches || !mobileViewport || !items.length) return;
      mobileViewport.innerHTML = buildMobileCardHtml(items[index]);
      updateMobileControls();
      updateStatus();
    };

    const goMobile = (direction) => {
      if (!mq.matches || items.length <= 1) return;
      markNavigated();
      index = (index + direction + items.length) % items.length;
      renderMobile();
    };

    const applyItems = (reviews) => {
      if (signal.aborted) return;

      const nextItems = reviews.length ? reviews : fallbacks;
      if (!nextItems.length) return;

      const preserveIndex = hydrated && (hasUserNavigated || isAnimating);
      items = nextItems;

      if (preserveIndex) {
        clampIndex();
      } else {
        index = 0;
      }

      hydrated = true;

      if (isAnimating) {
        pendingRefresh = true;
        updateControls();
        updateMobileControls();
        return;
      }

      refreshView();
      bindControls();
    };

    const start = (reviews) => {
      applyItems(reviews);
    };

    const handleSideKey = (event, direction) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      go(direction);
    };

    const bindControls = () => {
      if (controlsBound) return;
      controlsBound = true;

      slots.left?.addEventListener('click', () => go(-1));
      slots.right?.addEventListener('click', () => go(1));
      slots.left?.addEventListener('keydown', (event) => handleSideKey(event, -1));
      slots.right?.addEventListener('keydown', (event) => handleSideKey(event, 1));

      mobilePrev?.addEventListener('click', () => goMobile(-1));
      mobileNext?.addEventListener('click', () => goMobile(1));
      desktopPrev?.addEventListener('click', () => go(-1));
      desktopNext?.addEventListener('click', () => go(1));

      root.addEventListener('keydown', (event) => {
        if (mq.matches) {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goMobile(1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goMobile(-1);
          }
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          go(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          go(-1);
        }
      });

      mq.addEventListener('change', () => {
        if (mq.matches) {
          renderMobile();
        } else {
          render();
        }
      });
    };

    const clearIncoming = () => {
      if (!slots.incoming) return;
      slots.incoming.setAttribute('hidden', '');
      slots.incoming.setAttribute('aria-hidden', 'true');
      slots.incoming.removeAttribute('data-incoming');
      slots.incoming.innerHTML = '';
    };

    const prepareIncoming = (direction) => {
      if (!slots.incoming || items.length <= 1) return;

      const total = items.length;
      const review =
        direction > 0 ? items[(index + 2) % total] : items[(index - 2 + total) % total];

      fillSlot(slots.incoming, review, 'incoming');
      slots.incoming.dataset.incoming = direction > 0 ? 'next' : 'prev';
      slots.incoming.removeAttribute('hidden');
      slots.incoming.setAttribute('aria-hidden', 'false');
    };

    const finishDesktopSlide = (direction) => {
      index = (index + direction + items.length) % items.length;
      stage.classList.add('mct__stage--no-transition');
      stage.removeAttribute('data-slide');
      clearIncoming();
      render();
      stage.offsetWidth;
      stage.classList.remove('mct__stage--no-transition');
      isAnimating = false;
      updateControls();
      updateStatus();

      if (pendingRefresh) {
        pendingRefresh = false;
        refreshView();
      }
    };

    const go = (direction) => {
      if (mq.matches || isAnimating || items.length <= 1) return;

      markNavigated();

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) {
        index = (index + direction + items.length) % items.length;
        stage.removeAttribute('data-slide');
        clearIncoming();
        render();
        updateStatus();
        return;
      }

      isAnimating = true;
      prepareIncoming(direction);
      updateControls();
      updateStatus(direction);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (signal.aborted) return;
          stage.dataset.slide = direction > 0 ? 'next' : 'prev';
          waitForStageTransition().then(() => {
            if (signal.aborted) return;
            finishDesktopSlide(direction);
          });
        });
      });
    };

    const render = () => {
      if (mq.matches) return;

      clearIncoming();
      const total = items.length;
      const left = items[(index - 1 + total) % total];
      const center = items[index];
      const right = items[(index + 1) % total];

      fillSlot(slots.left, left, 'left');
      fillSlot(slots.center, center, 'center');
      fillSlot(slots.right, right, 'right');

      updateControls();
      updateStatus();
    };

    const updateControls = () => {
      const sideDisabled = mq.matches || isAnimating || items.length <= 1;
      const desktopDisabled = mq.matches || isAnimating || items.length <= 1;

      slots.left?.toggleAttribute('aria-disabled', sideDisabled);
      slots.right?.toggleAttribute('aria-disabled', sideDisabled);

      if (sideDisabled) {
        slots.left?.setAttribute('tabindex', '-1');
        slots.right?.setAttribute('tabindex', '-1');
      } else {
        slots.left?.setAttribute('tabindex', '0');
        slots.right?.setAttribute('tabindex', '0');
      }

      desktopPrev?.toggleAttribute('disabled', desktopDisabled);
      desktopNext?.toggleAttribute('disabled', desktopDisabled);
    };

    const updateMobileControls = () => {
      const disabled = items.length <= 1;
      mobilePrev?.toggleAttribute('disabled', disabled);
      mobileNext?.toggleAttribute('disabled', disabled);
    };

    const updateStatus = (direction) => {
      if (!statusEl || !items.length) return;
      const label = items[index]?.name || 'Testimonial';
      const verb = direction > 0 ? 'Next' : direction < 0 ? 'Previous' : 'Showing';
      statusEl.textContent = `${verb}: ${label}. ${index + 1} of ${items.length}.`;
    };

    bindControls();

    const currentLoad = ++loadId;

    const finishLoad = (reviews) => {
      if (signal.aborted || currentLoad !== loadId) return;
      start(reviews.length ? reviews : fallbacks);
    };

    if (token && shopDomain) {
      fetchReviews(token, shopDomain, sort, signal)
        .then(finishLoad)
        .catch(() => {
          if (signal.aborted || currentLoad !== loadId) return;
          return fetchCarouselReviews(token, shopDomain, sort, signal).then(finishLoad);
        })
        .catch(() => {
          if (signal.aborted || currentLoad !== loadId) return;
          start(fallbacks);
        });
    } else {
      start(fallbacks);
    }
  }

  async function fetchReviews(token, shopDomain, sort, signal) {
    const url = new URL('https://judge.me/api/v1/reviews');
    url.searchParams.set('api_token', token);
    url.searchParams.set('shop_domain', shopDomain);
    url.searchParams.set('per_page', '100');

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`Judge.me reviews ${response.status}`);

    const data = await response.json();
    const raw = Array.isArray(data.reviews) ? data.reviews : [];

    return sortReviews(
      raw
        .map((entry) => ({
          id: String(entry.id || entry.reviewer?.name || Math.random()),
          name: entry.reviewer?.name?.trim() || 'Anonymous',
          company: entry.title?.trim() || entry.product_title?.trim() || '',
          body: entry.body?.trim() || entry.title?.trim() || '',
          location: '',
          avatar: entry.reviewer?.avatar_url || '',
          rating: entry.rating || 0,
          date: entry.created_at || '',
        }))
        .filter((review) => review.body),
      sort
    );
  }

  async function fetchCarouselReviews(token, shopDomain, sort, signal) {
    const url = new URL('https://judge.me/api/v1/widgets/featured_carousel');
    url.searchParams.set('api_token', token);
    url.searchParams.set('shop_domain', shopDomain);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`Judge.me carousel ${response.status}`);

    const data = await response.json();
    const widgetHtml = data.featured_carousel || data.widget;
    if (!widgetHtml) throw new Error('Judge.me carousel empty');

    const doc = new DOMParser().parseFromString(widgetHtml, 'text/html');

    return sortReviews(
      Array.from(doc.querySelectorAll('.jdgm-carousel-item'))
        .map((el) => {
          const name =
            el.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent?.trim() || 'Anonymous';
          const body =
            el.querySelector('.jdgm-carousel-item__review-body p')?.textContent?.trim() ||
            el.querySelector('.jdgm-carousel-item__review-body')?.textContent?.trim() ||
            '';
          const title =
            el.querySelector('.jdgm-carousel-item__review-title')?.textContent?.trim() || '';

          return {
            id: el.getAttribute('data-review-id') || name,
            name,
            company: title,
            body: body || title,
            location: '',
            avatar: '',
            rating: 0,
            date: el.querySelector('.jdgm-carousel-item__timestamp')?.getAttribute('data-time') || '',
          };
        })
        .filter((review) => review.body),
      sort
    );
  }

  function sortReviews(reviews, sort) {
    if (sort === 'most_recent') {
      reviews.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    } else {
      reviews.sort((a, b) => b.rating - a.rating || parseDate(b.date) - parseDate(a.date));
    }
    return reviews;
  }

  function parseFallbacks(root) {
    return Array.from(root.querySelectorAll('[data-mct-fallback]'))
      .map((el) => ({
        id: el.dataset.id || el.textContent,
        name: el.dataset.name || '',
        company: el.dataset.company || '',
        body: el.dataset.body || '',
        location: el.dataset.location || '',
        avatar: el.dataset.avatar || '',
      }))
      .filter((item) => item.name);
  }

  function parseDate(value) {
    if (!value) return 0;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  function waitForStageTransition() {
    return new Promise((resolve) => {
      window.setTimeout(resolve, TRANSITION_MS + 50);
    });
  }

  function waitForTransition(element) {
    return new Promise((resolve) => {
      if (!element) {
        resolve();
        return;
      }

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        element.removeEventListener('transitionend', onEnd);
        window.clearTimeout(fallback);
        resolve();
      };

      const onEnd = (event) => {
        if (event.target !== element) return;
        const property = event.propertyName;
        if (property === 'transform' || property === '-webkit-transform') finish();
      };

      element.addEventListener('transitionend', onEnd);
      const fallback = window.setTimeout(finish, TRANSITION_MS + 80);
    });
  }

  function fillSlot(slot, review, position) {
    if (!slot) return;

    slot.className = 'mct__card';

    if (position === 'incoming') {
      slot.classList.add('mct__card--incoming');
      slot.removeAttribute('role');
      slot.removeAttribute('tabindex');
      slot.removeAttribute('aria-label');
    } else {
      slot.classList.add(`mct__card--pos-${position}`);
      if (position !== 'center') {
        slot.classList.add('mct__card--interactive');
        slot.setAttribute('role', 'button');
        slot.setAttribute('tabindex', '0');
        slot.setAttribute(
          'aria-label',
          position === 'right' ? `Next testimonial: ${review.name}` : `Previous testimonial: ${review.name}`
        );
      } else {
        slot.removeAttribute('role');
        slot.removeAttribute('tabindex');
        slot.removeAttribute('aria-label');
      }
    }

    slot.innerHTML = buildCardHtml(review);
  }

  function buildMobileCardHtml(review) {
    return `<article class="mct__mobile-card">${buildCardHtml(review)}</article>`;
  }

  function buildCardHtml(review) {
    const avatar = review.avatar
      ? `<img class="mct__avatar" src="${escapeHtml(review.avatar)}" alt="" width="86" height="86" loading="lazy">`
      : `<span class="mct__avatar mct__avatar--initials" aria-hidden="true">${escapeHtml(
          (review.name || 'A').trim().charAt(0).toUpperCase() || 'A'
        )}</span>`;

    const location = review.location
      ? `<div class="mct__location">${PIN_ICON}<span>${escapeHtml(review.location)}</span></div>`
      : '';

    return `
      <div class="mct__header">
        <div class="mct__avatar-wrap">${avatar}</div>
        <div class="mct__meta">
          ${review.name ? `<p class="mct__name">${escapeHtml(review.name)}</p>` : ''}
          ${review.company ? `<p class="mct__company">${escapeHtml(review.company)}</p>` : ''}
        </div>
      </div>
      <div class="mct__detail">
        ${review.body ? `<p class="mct__body">${escapeHtml(review.body)}</p>` : ''}
        ${location}
      </div>
    `;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function boot() {
    document.querySelectorAll('[data-matcha-client-testimonials]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.('[data-matcha-client-testimonials]');
    if (root) {
      root.dataset.mctReady = 'false';
      init(root);
    }
  });
})();
